import { FetchMethod, useCodeerApi } from "../useCodeerApi"
import {
  ClientChannelSchema,
  ClientChannelListSchema,
  ClientChannelOAuthBindingSchema,
  ClientChannelConfigSchema,
  WebClientConfigSchema,
  WidgetClientConfigSchema,
  ExtensionClientConfigSchema,
  SlackClientConfigSchema,
  LineClientConfigSchema,
  MessengerClientConfigSchema,
  InstagramClientConfigSchema,
  ClientChannelImageMetaSchema,
} from "./types"
import {
  Response,
  ClientChannelType,
  ClientChannelConfig,
  isInstagramClientConfig,
  isWebClientConfig,
  isWidgetClientConfig,
  isExtensionClientConfig,
  isMessengerClientConfig,
  isSlackClientConfig,
  isLineClientConfig,
  normalizeLineSkipKeywords,
} from "@/types"

// ===== ClientChannel API Response Types =====

export interface ClientChannelResponse extends ClientChannelSchema {}
export interface ClientChannelListResponse extends ClientChannelListSchema {}
export interface ClientChannelOAuthBindingResponse extends ClientChannelOAuthBindingSchema {}

export interface LineQuotaResponse {
  status: "available" | "unavailable"
  quota_type: "limited" | "none" | null
  limit: number | null
  total_usage: number | null
  checked_at: string
}

export const useGetLineQuotaApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) =>
  useCodeerApi<Response<LineQuotaResponse>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}/line-quota`
  )

export type ClientChannelHealthStatus =
  | "not_applicable"
  | "healthy"
  | "unhealthy"
  | "unavailable"

export type ClientChannelAgentOwnershipStatus =
  | "not_applicable"
  | "canonical"
  | "missing"
  | "ambiguous"

export type ClientChannelExternalHealthCoverageStatus =
  | "complete"
  | "incomplete"

export interface ClientChannelReadinessSummary {
  agent_count: number
  published_agent_count: number
  published_channel_count: number
  confirmed_ready_channel_count: number
  health_status: ClientChannelHealthStatus
  external_health_coverage_status: ClientChannelExternalHealthCoverageStatus
  ownership_status: ClientChannelAgentOwnershipStatus
  canonical_agent_ids: string[]
  live_web_channel: {
    id: string
    slug: string
    last_published_slug?: string | null
  } | null
}

// ===== ClientChannel Request Types =====

export interface CreateClientChannelRequest {
  name?: string
  slug: string
  type: ClientChannelType
  config: ClientChannelConfigSchema
}

export const makeCreateClientChannelRequest = (
  name: string,
  slug: string,
  type: ClientChannelType,
  config: ClientChannelConfig
): CreateClientChannelRequest => {
  return {
    name,
    slug,
    type,
    config: makeClientChannelConfigSchema(config),
  }
}

export interface UpdateClientChannelRequest {
  name?: string
  slug?: string
  config?: ClientChannelConfigSchema
  widget_private_auth?: UpdateWidgetPrivateAuthRequest
}

export interface UpdateWidgetPrivateAuthRequest {
  issuer?: string | null
}

export interface WidgetSecretRevealResponse {
  shared_secret: string
  secret_last_regenerated_at: string
}

export const makeClientChannelConfigSchema = (
  config: ClientChannelConfig
): ClientChannelConfigSchema => {
  // Each config MUST include the `type` field for discriminator-based validation
  if (isWebClientConfig(config)) {
    return {
      type: "web",
      title: config.title,
      descriptions: config.descriptions,
      allow_anonymous_access: config.allowAnonymousAccess,
      valid_redeem_code: config.validRedeemCode,
      ad_buttons: config.adButtons,
      // cannot update login image and header image here
    } as WebClientConfigSchema
  }
  if (isWidgetClientConfig(config)) {
    return {
      type: "widget",
      agent_id: config.agentId || null,
      allow_anonymous_access: config.allowAnonymousAccess,
      login_enabled: config.loginEnabled,
      login_verification_mode: config.loginVerificationMode,
      origin_policy_mode: config.originPolicyMode,
      allowed_origins: config.allowedOrigins,
      ui: {
        title: config.ui.title,
        position: config.ui.position,
        locale: config.ui.locale,
        launcher: {
          label: config.ui.launcher.label,
        },
        panel: {
          greeting: config.ui.panel.greeting,
          suggestions: config.ui.panel.suggestions || [],
        },
        theme: {
          base_color: config.ui.theme.baseColor,
        },
      },
    } as WidgetClientConfigSchema
  }
  if (isExtensionClientConfig(config)) {
    return {
      type: "browser_extension",
      extension_id: config.extensionId,
    } as ExtensionClientConfigSchema
  }
  if (isSlackClientConfig(config)) {
    return {
      type: "slack",
      bot_token: config.botToken || "",
      signing_secret: config.signingSecret || "",
      team_id: config.teamId || "",
      team_name: config.teamName || "",
      bot_user_id: config.botUserId || null,
      // agent_id must be a valid UUID or null, not empty string
      agent_id: config.agentId || null,
    } as SlackClientConfigSchema
  }
  if (isLineClientConfig(config)) {
    return {
      type: "line",
      channel_secret: config.channelSecret || "",
      channel_access_token: config.channelAccessToken || "",
      liff_id: config.liffId || "",
      line_login_channel_id: config.lineLoginChannelId || "",
      agent_id: config.agentId || null,
      require_email_verification: config.requireEmailVerification,
      skip_keywords: normalizeLineSkipKeywords(config.skipKeywords),
      conversation_session_timeout_hours:
        config.conversationSessionTimeoutHours ?? 24,
    } as LineClientConfigSchema
  }
  if (isMessengerClientConfig(config)) {
    return {
      type: "messenger",
      page_id: config.pageId || "",
      webhook_verify_token: config.webhookVerifyToken || "",
      meta_app_secret: config.metaAppSecret || "",
      access_token: config.accessToken || "",
      agent_id: config.agentId || null,
      conversation_session_timeout_hours:
        config.conversationSessionTimeoutHours ?? 24,
    } as MessengerClientConfigSchema
  }
  if (isInstagramClientConfig(config)) {
    return {
      type: "instagram",
      account_id: config.accountId || "",
      webhook_verify_token: config.webhookVerifyToken || "",
      meta_app_secret: config.metaAppSecret || "",
      access_token: config.accessToken || "",
      agent_id: config.agentId || null,
      conversation_session_timeout_hours:
        config.conversationSessionTimeoutHours ?? 24,
    } as InstagramClientConfigSchema
  }
  throw new Error("Invalid configuration type")
}

export const makeUpdateClientChannelRequest = (
  name?: string,
  slug?: string,
  config?: ClientChannelConfig,
  widgetPrivateAuth?: UpdateWidgetPrivateAuthRequest
): UpdateClientChannelRequest => {
  return {
    name,
    slug,
    config: config ? makeClientChannelConfigSchema(config) : undefined,
    widget_private_auth: widgetPrivateAuth,
  }
}

export interface PublishClientChannelRequest {
  version: string
}

export interface SlugAvailabilityResponse {
  slug: string
  available: boolean
}

export interface UploadClientChannelImageResponse extends ClientChannelImageMetaSchema {}

export const useUploadClientChannelImageApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) => {
  return useCodeerApi<Response<UploadClientChannelImageResponse>, FormData>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}/upload-image`,
    FetchMethod.Post
  )
}

// ===== OAuth Binding Request Types =====

export interface CreateClientChannelOAuthBindingRequest {
  oauth_provider_id: string
}

// ===== Response Transformers =====

export {
  transToClientChannel as clientChannelResponseToClientChannel,
  transToClientChannelListItems as clientChannelsResponseToClientChannels,
  transToClientChannelOAuthBinding as oauthBindingResponseToOAuthBinding,
  transToClientChannelOAuthBindings as oauthBindingsResponseToOAuthBindings,
} from "./types"

// ===== ClientChannel CRUD API Hooks =====

/**
 * Get all client channels for a workspace (paginated)
 */
export const useGetClientChannelsApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientChannelListResponse[]>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels`,
    FetchMethod.Get
  )
}

export const useGetClientChannelReadinessSummaryApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<ClientChannelReadinessSummary>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/readiness-summary`,
    FetchMethod.Get
  )
}

/**
 * Get a single client channel by ID
 */
export const useGetClientChannelApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) => {
  return useCodeerApi<Response<ClientChannelResponse>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}`,
    FetchMethod.Get
  )
}

/**
 * Create a new client channel
 */
export const useCreateClientChannelApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<
    Response<ClientChannelResponse>,
    CreateClientChannelRequest
  >(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels`,
    FetchMethod.Post
  )
}

/**
 * Update an existing client channel
 */
export const useUpdateClientChannelApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) => {
  return useCodeerApi<
    Response<ClientChannelResponse>,
    UpdateClientChannelRequest
  >(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}`,
    FetchMethod.Put
  )
}

/**
 * Delete a client channel
 */
export const useDeleteClientChannelApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) => {
  return useCodeerApi<Response<unknown>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}`,
    FetchMethod.Delete
  )
}

/**
 * Publish a client channel (upload config.json to S3)
 */
export const usePublishClientChannelApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) => {
  return useCodeerApi<
    Response<ClientChannelResponse>,
    PublishClientChannelRequest
  >(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}/publish`,
    FetchMethod.Post
  )
}

/**
 * Unpublish a client channel (remove config.json from S3)
 */
export const useUnpublishClientChannelApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) => {
  return useCodeerApi<Response<ClientChannelResponse>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}/unpublish`,
    FetchMethod.Post
  )
}

/**
 * Check if a slug is available
 */
export const useCheckSlugAvailabilityApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<SlugAvailabilityResponse>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/slug-availability`,
    FetchMethod.Get
  )
}

export const useGenerateWidgetPrivateAuthSecretApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) => {
  return useCodeerApi<Response<WidgetSecretRevealResponse>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}/widget-private-auth/secret`,
    FetchMethod.Post
  )
}

export const useRevokeWidgetPrivateAuthSecretApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) => {
  return useCodeerApi<Response<ClientChannelResponse>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}/widget-private-auth/secret`,
    FetchMethod.Delete
  )
}

// ===== Client Channel OAuth Bindings API Hooks =====

/**
 * Get all OAuth bindings for a client channel
 */
export const useGetClientChannelOAuthBindingsApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) => {
  return useCodeerApi<Response<ClientChannelOAuthBindingResponse[]>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}/oauth/bindings`,
    FetchMethod.Get
  )
}

/**
 * Create a new OAuth binding for a client channel
 */
export const useCreateClientChannelOAuthBindingApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string
) => {
  return useCodeerApi<
    Response<ClientChannelOAuthBindingResponse>,
    CreateClientChannelOAuthBindingRequest
  >(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}/oauth/bindings`,
    FetchMethod.Post
  )
}

/**
 * Delete an OAuth binding
 */
export const useDeleteClientChannelOAuthBindingApi = (
  organizationId: string,
  workspaceId: string,
  channelId: string,
  bindingId: string
) => {
  return useCodeerApi<Response<unknown>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/client-channels/${channelId}/oauth/bindings/${bindingId}`,
    FetchMethod.Delete
  )
}
